import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CURRENT_TERMS_VERSION,
  CURRENT_PRIVACY_VERSION,
} from '@klaro7/shared-types';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  Alert,
  AlertDescription,
} from '@klaro7/ui';

import { DateOfBirthInput } from '@/components/DateOfBirthInput';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { completeOnboarding } from '@/features/auth/services/auth.service';

/** Returns the age in completed years for a given ISO date string, or null. */
function getAge(iso: string): number | null {
  if (!iso) return null;
  const dob = new Date(iso);
  if (Number.isNaN(dob.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const m = today.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age -= 1;
  return age;
}

export function CompletaProfiloPage() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();

  const needsDob = !user?.profile?.dateOfBirth;
  const needsTerms = user?.consents?.termsVersion !== CURRENT_TERMS_VERSION;
  const needsPrivacy = user?.consents?.privacyVersion !== CURRENT_PRIVACY_VERSION;

  const [dateOfBirth, setDateOfBirth] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const age = getAge(dateOfBirth);
  const isAdult = age !== null && age >= 18;
  const dobBlocking = needsDob && age !== null && !isAdult;

  const canSubmit =
    (!needsDob || isAdult) &&
    (!needsTerms || acceptTerms) &&
    (!needsPrivacy || acceptPrivacy);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (needsDob && !isAdult) {
      setError('Account non idoneo: devi essere maggiorenne per usare i servizi');
      return;
    }
    if (needsTerms && !acceptTerms) {
      setError('Devi accettare i Termini e Condizioni');
      return;
    }
    if (needsPrivacy && !acceptPrivacy) {
      setError('Devi accettare l’Informativa Privacy');
      return;
    }

    setIsLoading(true);
    try {
      await completeOnboarding({
        dateOfBirth: needsDob ? dateOfBirth : undefined,
        acceptTerms: true,
        acceptPrivacy: true,
      });
      await refreshUser();
      // No bare "/my" route exists; the regular user's home is "/".
      navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Operazione non riuscita. Riprova.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl px-4 py-6 sm:px-6 lg:px-8">
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Completa il tuo profilo</h1>
          <p className="text-neutral-500">
            Per continuare a usare Klaro7 abbiamo bisogno di alcune informazioni e del tuo consenso.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Dati e consensi</CardTitle>
            <CardDescription>
              Questi dati sono richiesti per legge e devono essere completati una sola volta.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <Alert variant="error">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {needsDob && (
                <DateOfBirthInput
                  value={dateOfBirth}
                  onChange={setDateOfBirth}
                  required
                  error={
                    dobBlocking
                      ? 'Account non idoneo: devi essere maggiorenne per usare i servizi'
                      : undefined
                  }
                  helperText="Devi essere maggiorenne per usare i servizi"
                />
              )}

              {needsTerms && (
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="onboarding-terms"
                    checked={acceptTerms}
                    onChange={(e) => setAcceptTerms(e.target.checked)}
                    className="h-4 w-4 mt-0.5 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                  />
                  <label htmlFor="onboarding-terms" className="text-sm text-neutral-600 cursor-pointer">
                    Ho letto e accetto i{' '}
                    <a
                      href="https://klaro7.com/termini"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 underline hover:text-primary-700"
                    >
                      Termini e Condizioni
                    </a>
                    {' '}<span className="text-error">*</span>
                  </label>
                </div>
              )}

              {needsPrivacy && (
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="onboarding-privacy"
                    checked={acceptPrivacy}
                    onChange={(e) => setAcceptPrivacy(e.target.checked)}
                    className="h-4 w-4 mt-0.5 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                  />
                  <label htmlFor="onboarding-privacy" className="text-sm text-neutral-600 cursor-pointer">
                    Ho letto e accetto l&apos;
                    <a
                      href="https://klaro7.com/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 underline hover:text-primary-700"
                    >
                      Informativa Privacy
                    </a>
                    {' '}<span className="text-error">*</span>
                  </label>
                </div>
              )}

              <Button type="submit" className="w-full" loading={isLoading} disabled={!canSubmit}>
                Conferma e continua
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
